import { useEffect, useRef, useCallback } from 'react';

type AmbientType = 'default' | 'scary' | 'night' | 'truth' | 'identity' | 'time' | 'mortality' | 'nothing' | 'meta' | 'daily' | 'voice' | 'visual' | 'seasonal' | 'premium' | 'games' | 'experimental' | 'dreams' | 'creativity' | 'fear' | 'future';

class AmbientAudioEngine {
  private audioContext: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private oscillators: OscillatorNode[] = [];
  private noiseNodes: AudioBufferSourceNode[] = [];
  private intervalIds: number[] = [];
  private isPlaying = false;
  private currentType: AmbientType = 'default';
  private reverbNode: ConvolverNode | null = null;
  private fadeDuration = 0.8; // Fade duration in seconds
  private isFading = false;

  private getAudioContext(): AudioContext {
    if (!this.audioContext) {
      this.audioContext = new AudioContext();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0; // Start at 0 for fade-in
      this.masterGain.connect(this.audioContext.destination);
    }
    return this.audioContext;
  }

  private createNoiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = buffer.getChannelData(0);
    
    for (let i = 0; i < bufferSize; i++) {
      output[i] = Math.random() * 2 - 1;
    }
    return buffer;
  }

  private createBrownNoise(ctx: AudioContext, gain: GainNode) {
    const bufferSize = ctx.sampleRate * 4;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const output = buffer.getChannelData(0);
    
    let lastOut = 0;
    for (let i = 0; i < bufferSize; i++) {
      const white = Math.random() * 2 - 1;
      output[i] = (lastOut + 0.02 * white) / 1.02;
      lastOut = output[i];
      output[i] *= 3.5;
    }
    
    const noise = ctx.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    
    noise.connect(filter);
    filter.connect(gain);
    noise.start();
    
    return noise;
  }

  private stopAll() {
    this.oscillators.forEach(osc => {
      try { osc.stop(); } catch {}
    });
    this.oscillators = [];
    
    this.noiseNodes.forEach(noise => {
      try { noise.stop(); } catch {}
    });
    this.noiseNodes = [];

    this.intervalIds.forEach(id => clearInterval(id));
    this.intervalIds = [];
  }

  private targetVolume = 0.15;

  start(type: AmbientType = 'default') {
    if (this.isPlaying && this.currentType === type) return;
    
    // If already playing a different track, crossfade
    if (this.isPlaying && this.currentType !== type) {
      this.crossfadeTo(type);
      return;
    }
    
    this.currentType = type;
    
    const ctx = this.getAudioContext();
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const gain = ctx.createGain();
    gain.connect(this.masterGain!);

    switch (type) {
      case 'scary':
        this.createScaryAmbient(ctx, gain);
        break;
      case 'night':
        this.createNightAmbient(ctx, gain);
        break;
      case 'mortality':
      case 'time':
        this.createMortalityAmbient(ctx, gain);
        break;
      case 'truth':
        this.createTruthAmbient(ctx, gain);
        break;
      case 'identity':
        this.createIdentityAmbient(ctx, gain);
        break;
      case 'nothing':
        this.createNothingAmbient(ctx, gain);
        break;
      case 'meta':
        this.createMetaAmbient(ctx, gain);
        break;
      case 'daily':
        this.createDailyAmbient(ctx, gain);
        break;
      case 'seasonal':
        this.createSeasonalAmbient(ctx, gain);
        break;
      case 'premium':
        this.createPremiumAmbient(ctx, gain);
        break;
      case 'dreams':
        this.createDreamsAmbient(ctx, gain);
        break;
      case 'creativity':
        this.createCreativityAmbient(ctx, gain);
        break;
      case 'fear':
        this.createFearAmbient(ctx, gain);
        break;
      case 'future':
        this.createFutureAmbient(ctx, gain);
        break;
      default:
        this.createDefaultAmbient(ctx, gain);
    }

    this.isPlaying = true;
    
    // Fade in
    this.fadeIn();
  }

  private fadeIn() {
    if (!this.masterGain || !this.audioContext) return;
    
    const now = this.audioContext.currentTime;
    this.masterGain.gain.cancelScheduledValues(now);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
    this.masterGain.gain.linearRampToValueAtTime(this.targetVolume, now + this.fadeDuration);
  }

  private fadeOut(): Promise<void> {
    return new Promise((resolve) => {
      if (!this.masterGain || !this.audioContext) {
        resolve();
        return;
      }
      
      const now = this.audioContext.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
      this.masterGain.gain.linearRampToValueAtTime(0, now + this.fadeDuration);
      
      setTimeout(resolve, this.fadeDuration * 1000);
    });
  }

  private async crossfadeTo(newType: AmbientType) {
    if (this.isFading) return;
    this.isFading = true;
    
    // Fade out current
    await this.fadeOut();
    
    // Stop current sounds
    this.stopAll();
    
    // Start new ambient
    this.currentType = newType;
    
    const ctx = this.getAudioContext();
    const gain = ctx.createGain();
    gain.connect(this.masterGain!);

    switch (newType) {
      case 'scary':
        this.createScaryAmbient(ctx, gain);
        break;
      case 'night':
        this.createNightAmbient(ctx, gain);
        break;
      case 'mortality':
      case 'time':
        this.createMortalityAmbient(ctx, gain);
        break;
      case 'truth':
        this.createTruthAmbient(ctx, gain);
        break;
      case 'identity':
        this.createIdentityAmbient(ctx, gain);
        break;
      case 'nothing':
        this.createNothingAmbient(ctx, gain);
        break;
      case 'meta':
        this.createMetaAmbient(ctx, gain);
        break;
      case 'daily':
        this.createDailyAmbient(ctx, gain);
        break;
      case 'seasonal':
        this.createSeasonalAmbient(ctx, gain);
        break;
      case 'premium':
        this.createPremiumAmbient(ctx, gain);
        break;
      case 'dreams':
        this.createDreamsAmbient(ctx, gain);
        break;
      case 'creativity':
        this.createCreativityAmbient(ctx, gain);
        break;
      case 'fear':
        this.createFearAmbient(ctx, gain);
        break;
      case 'future':
        this.createFutureAmbient(ctx, gain);
        break;
      default:
        this.createDefaultAmbient(ctx, gain);
    }
    
    // Fade in new
    this.fadeIn();
    this.isFading = false;
  }

  private createDefaultAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.08;
    
    // Soft drone
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 60;
    
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.3;
    
    osc.connect(oscGain);
    oscGain.connect(gain);
    osc.start();
    this.oscillators.push(osc);
    
    // Very quiet brown noise
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  private createScaryAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.12;
    
    // Low rumble
    const rumble = ctx.createOscillator();
    rumble.type = 'sine';
    rumble.frequency.value = 35;
    
    const rumbleGain = ctx.createGain();
    rumbleGain.gain.value = 0.5;
    
    // Add subtle LFO modulation
    const lfo = ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.1;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 5;
    lfo.connect(lfoGain);
    lfoGain.connect(rumble.frequency);
    lfo.start();
    
    rumble.connect(rumbleGain);
    rumbleGain.connect(gain);
    rumble.start();
    
    this.oscillators.push(rumble, lfo);
    
    // Eerie high tone
    const eerie = ctx.createOscillator();
    eerie.type = 'sine';
    eerie.frequency.value = 440;
    
    const eerieGain = ctx.createGain();
    eerieGain.gain.value = 0.02;
    
    // Slow vibrato
    const vibrato = ctx.createOscillator();
    vibrato.frequency.value = 0.3;
    const vibratoGain = ctx.createGain();
    vibratoGain.gain.value = 10;
    vibrato.connect(vibratoGain);
    vibratoGain.connect(eerie.frequency);
    vibrato.start();
    
    eerie.connect(eerieGain);
    eerieGain.connect(gain);
    eerie.start();
    
    this.oscillators.push(eerie, vibrato);
    
    // Dark noise
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  private createNightAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.06;
    
    // Very deep drone
    const drone = ctx.createOscillator();
    drone.type = 'sine';
    drone.frequency.value = 40;
    
    const droneGain = ctx.createGain();
    droneGain.gain.value = 0.4;
    
    drone.connect(droneGain);
    droneGain.connect(gain);
    drone.start();
    this.oscillators.push(drone);
    
    // Gentle wind-like noise
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  private createMortalityAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.05;
    
    // Heartbeat-like pulse
    const pulse = ctx.createOscillator();
    pulse.type = 'sine';
    pulse.frequency.value = 50;
    
    const pulseGain = ctx.createGain();
    pulseGain.gain.value = 0;
    
    // Heartbeat envelope
    const now = ctx.currentTime;
    const beatLength = 0.8;
    
    const scheduleHeartbeat = () => {
      const time = ctx.currentTime;
      pulseGain.gain.setValueAtTime(0, time);
      pulseGain.gain.linearRampToValueAtTime(0.3, time + 0.05);
      pulseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.15);
      pulseGain.gain.setValueAtTime(0.01, time + 0.2);
      pulseGain.gain.linearRampToValueAtTime(0.2, time + 0.25);
      pulseGain.gain.exponentialRampToValueAtTime(0.01, time + 0.4);
    };
    
    scheduleHeartbeat();
    setInterval(scheduleHeartbeat, beatLength * 1000);
    
    pulse.connect(pulseGain);
    pulseGain.connect(gain);
    pulse.start();
    this.oscillators.push(pulse);
  }

  private createTruthAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.04;
    
    // Clean, clear tone
    const tone = ctx.createOscillator();
    tone.type = 'sine';
    tone.frequency.value = 220;
    
    const toneGain = ctx.createGain();
    toneGain.gain.value = 0.08;
    
    tone.connect(toneGain);
    toneGain.connect(gain);
    tone.start();
    this.oscillators.push(tone);
    
    // Subtle harmonic
    const harmonic = ctx.createOscillator();
    harmonic.type = 'sine';
    harmonic.frequency.value = 330;
    
    const harmGain = ctx.createGain();
    harmGain.gain.value = 0.03;
    
    harmonic.connect(harmGain);
    harmGain.connect(gain);
    harmonic.start();
    this.oscillators.push(harmonic);
  }

  private createIdentityAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.05;
    
    // Reflective, slightly detuned
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 196;
    
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 198; // Slight detune for beating
    
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.15;
    
    osc1.connect(oscGain);
    osc2.connect(oscGain);
    oscGain.connect(gain);
    
    osc1.start();
    osc2.start();
    this.oscillators.push(osc1, osc2);
  }

  private createNothingAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.02;
    
    // Almost silence - just the faintest hum
    const hum = ctx.createOscillator();
    hum.type = 'sine';
    hum.frequency.value = 60;
    
    const humGain = ctx.createGain();
    humGain.gain.value = 0.1;
    
    hum.connect(humGain);
    humGain.connect(gain);
    hum.start();
    this.oscillators.push(hum);
  }

  private createMetaAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.04;
    
    // Digital, glitchy feel
    const osc = ctx.createOscillator();
    osc.type = 'square';
    osc.frequency.value = 80;
    
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;
    
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.05;
    
    osc.connect(filter);
    filter.connect(oscGain);
    oscGain.connect(gain);
    osc.start();
    this.oscillators.push(osc);
  }

  private createDailyAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.03;
    
    // Warm, welcoming
    const warm = ctx.createOscillator();
    warm.type = 'sine';
    warm.frequency.value = 130;
    
    const warmGain = ctx.createGain();
    warmGain.gain.value = 0.2;
    
    warm.connect(warmGain);
    warmGain.connect(gain);
    warm.start();
    this.oscillators.push(warm);
  }

  private createSeasonalAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.08;
    
    // Mystical, magical feel
    const base = ctx.createOscillator();
    base.type = 'sine';
    base.frequency.value = 110;
    
    const baseGain = ctx.createGain();
    baseGain.gain.value = 0.25;
    
    base.connect(baseGain);
    baseGain.connect(gain);
    base.start();
    this.oscillators.push(base);
    
    // Sparkle effect with high frequency shimmer
    const shimmer = ctx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.value = 880;
    
    const shimmerGain = ctx.createGain();
    shimmerGain.gain.value = 0;
    
    // Create random sparkle pattern
    const sparkle = () => {
      const now = ctx.currentTime;
      shimmerGain.gain.setValueAtTime(0, now);
      shimmerGain.gain.linearRampToValueAtTime(0.03, now + 0.02);
      shimmerGain.gain.exponentialRampToValueAtTime(0.001, now + 0.3);
    };
    
    const intervalId = setInterval(sparkle, 500 + Math.random() * 1000) as unknown as number;
    this.intervalIds.push(intervalId);
    
    shimmer.connect(shimmerGain);
    shimmerGain.connect(gain);
    shimmer.start();
    this.oscillators.push(shimmer);
    
    // Wind-like background
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  private createPremiumAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.1;
    
    // Deep, rich drone with harmonics
    const fundamental = ctx.createOscillator();
    fundamental.type = 'sine';
    fundamental.frequency.value = 55; // A1
    
    const fundamentalGain = ctx.createGain();
    fundamentalGain.gain.value = 0.4;
    
    fundamental.connect(fundamentalGain);
    fundamentalGain.connect(gain);
    fundamental.start();
    this.oscillators.push(fundamental);
    
    // First harmonic
    const harmonic1 = ctx.createOscillator();
    harmonic1.type = 'sine';
    harmonic1.frequency.value = 110;
    
    const harm1Gain = ctx.createGain();
    harm1Gain.gain.value = 0.2;
    
    harmonic1.connect(harm1Gain);
    harm1Gain.connect(gain);
    harmonic1.start();
    this.oscillators.push(harmonic1);
    
    // Second harmonic with subtle detune for richness
    const harmonic2 = ctx.createOscillator();
    harmonic2.type = 'sine';
    harmonic2.frequency.value = 165;
    
    const harm2Gain = ctx.createGain();
    harm2Gain.gain.value = 0.1;
    
    harmonic2.connect(harm2Gain);
    harm2Gain.connect(gain);
    harmonic2.start();
    this.oscillators.push(harmonic2);
    
    // Slow evolving pad
    const pad = ctx.createOscillator();
    pad.type = 'sine';
    pad.frequency.value = 220;
    
    const padGain = ctx.createGain();
    padGain.gain.value = 0.05;
    
    // Slow LFO for movement
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.05;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 15;
    lfo.connect(lfoGain);
    lfoGain.connect(pad.frequency);
    lfo.start();
    
    pad.connect(padGain);
    padGain.connect(gain);
    pad.start();
    this.oscillators.push(pad, lfo);
    
    // Subtle noise texture
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  // v4.0 New ambient types
  private createDreamsAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.06;
    // Ethereal pad with slow detuned oscillators
    const osc1 = ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 174; // F3
    const osc2 = ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = 176; // Slight detune for dreamy beating
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.15;
    osc1.connect(oscGain);
    osc2.connect(oscGain);
    oscGain.connect(gain);
    osc1.start();
    osc2.start();
    this.oscillators.push(osc1, osc2);
    // High shimmer
    const shimmer = ctx.createOscillator();
    shimmer.type = 'sine';
    shimmer.frequency.value = 523; // C5
    const shimGain = ctx.createGain();
    shimGain.gain.value = 0.02;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.08;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 20;
    lfo.connect(lfoGain);
    lfoGain.connect(shimmer.frequency);
    lfo.start();
    shimmer.connect(shimGain);
    shimGain.connect(gain);
    shimmer.start();
    this.oscillators.push(shimmer, lfo);
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  private createCreativityAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.05;
    // Warm major chord tones
    const notes = [261.63, 329.63, 392]; // C E G
    notes.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = freq;
      const g = ctx.createGain();
      g.gain.value = 0.06 - i * 0.015;
      osc.connect(g);
      g.connect(gain);
      osc.start();
      this.oscillators.push(osc);
    });
  }

  private createFearAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.10;
    // Dissonant low rumble
    const rumble = ctx.createOscillator();
    rumble.type = 'sawtooth';
    rumble.frequency.value = 30;
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 80;
    const rumGain = ctx.createGain();
    rumGain.gain.value = 0.3;
    rumble.connect(filter);
    filter.connect(rumGain);
    rumGain.connect(gain);
    rumble.start();
    this.oscillators.push(rumble);
    // Dissonant tritone
    const dis = ctx.createOscillator();
    dis.type = 'sine';
    dis.frequency.value = 185; // F#3
    const disGain = ctx.createGain();
    disGain.gain.value = 0.03;
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.15;
    const lfoG = ctx.createGain();
    lfoG.gain.value = 8;
    lfo.connect(lfoG);
    lfoG.connect(dis.frequency);
    lfo.start();
    dis.connect(disGain);
    disGain.connect(gain);
    dis.start();
    this.oscillators.push(dis, lfo);
    this.noiseNodes.push(this.createBrownNoise(ctx, gain));
  }

  private createFutureAmbient(ctx: AudioContext, gain: GainNode) {
    gain.gain.value = 0.05;
    // Ascending tones - hopeful
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = 220; // A3
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.12;
    // Slow ascending LFO
    const lfo = ctx.createOscillator();
    lfo.frequency.value = 0.02;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = 30;
    lfo.connect(lfoGain);
    lfoGain.connect(osc.frequency);
    lfo.start();
    osc.connect(oscGain);
    oscGain.connect(gain);
    osc.start();
    this.oscillators.push(osc, lfo);
    // Fifth harmony
    const fifth = ctx.createOscillator();
    fifth.type = 'sine';
    fifth.frequency.value = 330; // E4
    const fGain = ctx.createGain();
    fGain.gain.value = 0.06;
    fifth.connect(fGain);
    fGain.connect(gain);
    fifth.start();
    this.oscillators.push(fifth);
  }

  async stop() {
    if (!this.isPlaying) return;
    
    // Fade out before stopping
    await this.fadeOut();
    this.stopAll();
    this.isPlaying = false;
  }

  setVolume(volume: number) {
    this.targetVolume = Math.max(0, Math.min(1, volume));
    if (this.masterGain && this.audioContext && !this.isFading) {
      const now = this.audioContext.currentTime;
      this.masterGain.gain.cancelScheduledValues(now);
      this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, now);
      this.masterGain.gain.linearRampToValueAtTime(this.targetVolume, now + 0.1);
    }
  }

  isActive() {
    return this.isPlaying;
  }
}

// Singleton instance
let ambientEngine: AmbientAudioEngine | null = null;

export const getAmbientEngine = (): AmbientAudioEngine => {
  if (!ambientEngine) {
    ambientEngine = new AmbientAudioEngine();
  }
  return ambientEngine;
};

export const useAmbientAudio = (category?: string) => {
  const engineRef = useRef<AmbientAudioEngine | null>(null);

  useEffect(() => {
    engineRef.current = getAmbientEngine();
  }, []);

  const start = useCallback((type?: AmbientType) => {
    const ambientType = (type || category || 'default') as AmbientType;
    engineRef.current?.start(ambientType);
  }, [category]);

  const stop = useCallback(() => {
    engineRef.current?.stop();
  }, []);

  const setVolume = useCallback((volume: number) => {
    engineRef.current?.setVolume(volume);
  }, []);

  return { start, stop, setVolume };
};

export type { AmbientType };
